import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ChartType, ChartOptions, ChartDataset, LegendItem } from 'chart.js';
import { merge, sumBy } from 'lodash';
import { BaseChartDirective } from 'ng2-charts';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'chart-component',
	templateUrl: 'chart.component.html',
	styleUrls: ['./chart.component.scss'],
})
export class ChartComponent implements OnInit {
	@ViewChild(BaseChartDirective) public readonly chartDirective: BaseChartDirective;

	@Input() public data: Array<ChartDataset>;
	@Input() public labels: Array<string | Array<string>>;
	@Input() public chartType: ChartType;
	@Input() public options: ChartOptions = {};
	@Input() public canToggleLegend: boolean = false;
	@Input() public canToggleLegendItems: boolean = false;
	public readonly chartPlugins: Array<any> = [ChartDataLabels];

	public type: { [type: string]: boolean };
	public legendItemsAreOff: boolean = false;
	public pieTotal: number;

	public chartOptions: ChartOptions = {
		responsive: true,
		plugins: {
			title: {
				display: false,
				text: '',
			},
			legend: {
				display: true,
			},
		},
	};

	constructor(private readonly translateService: TranslateService) {}

	public ngOnInit(): void {
		// type set is needed for AOT
		this.type = {
			line: this.chartType === 'line',
			bar: this.chartType === 'bar',
			pie: this.chartType === 'pie',
		};
		this.translateLabels();
		this.setChartOptions();
	}

	public toggleLegend(): void {
		this.chartOptions.plugins.legend.display = !this.chartOptions.plugins.legend.display;
		this.chartDirective.chart.options.plugins.legend.display = this.chartOptions.plugins.legend.display;
		this.chartDirective.chart.update();
	}

	public toggleAllLegendItems(): void {
		const chart = this.chartDirective.chart;
		const legendItems: Array<LegendItem> = (chart as any).legend.legendItems;
		this.legendItemsAreOff = !this.legendItemsAreOff;

		for (const [index, item] of legendItems.entries()) {
			const meta = chart.getDatasetMeta(index);
			chart.setDatasetVisibility(item.index, this.legendItemsAreOff);
			meta.hidden = this.legendItemsAreOff;
			item.hidden = this.legendItemsAreOff;
		}

		this.chartDirective.chart.update();
	}

	private translateLabels(): void {
		if (this.chartType === 'pie') {
			this.labels = this.labels.map((label) => this.translateService.instant(label.toString()));
		} else {
			for (const data of this.data) {
				(data as ChartDataset).label = this.translateService.instant((data as ChartDataset).label);
			}
		}
	}

	private setChartOptions(): void {
		this.chartOptions = merge(this.chartOptions, this.options);

		if (this.chartType === 'pie') {
			this.setTotalForPieChart();
			this.setLabelsForPieChart();
		}

		if (!this.chartOptions.plugins.legend.position) {
			this.chartOptions.plugins.legend.position = this.chartType === 'pie' ? 'right' : 'top';
		}

		if (this.chartType !== 'pie' && !this.chartOptions.scales.y) {
			this.chartOptions.scales.y = {
				beginAtZero: true,
			};
		}
	}

	private setLabelsForPieChart(): void {
		if (!this.chartOptions.plugins.datalabels) {
			this.chartOptions.plugins.datalabels = {
				formatter: (value, _context) => `${Math.round((value / this.pieTotal) * 100)}%`,
				font: {
					family: "'Open Sans', 'sans-serif'",
					size: 15,
				},
				display: 'auto',
				color: '#262626',
			};
		}
	}

	private setTotalForPieChart(): void {
		this.pieTotal = sumBy(this.data[0].data, (data) => parseFloat(data as unknown as string));
		this.chartOptions.plugins.title = {
			display: true,
			text: `${this.translateService.instant('TOTAL')}: ${this.getTotalAsRoundedString(this.pieTotal)}`,
		};

		this.chartOptions.plugins.legend.onClick = (event, item) => {
			const chart = this.chartDirective.chart;

			const value = parseFloat(this.chartDirective.datasets[0].data[item.index] as any);

			this.pieTotal = chart.getDataVisibility(item.index) ? this.pieTotal - value : this.pieTotal + value;
			chart.toggleDataVisibility(item.index);
			this.chartOptions.plugins.title.text = `${this.translateService.instant(
				'TOTAL'
			)}: ${this.getTotalAsRoundedString(this.pieTotal)}`;
			chart.options.plugins.title.text = this.chartOptions.plugins.title.text;

			chart.update();
		};
	}

	private getTotalAsRoundedString(_total: number): string {
		return _total % 1 === 0 ? _total.toString() : _total.toFixed(2);
	}
}
